import React, { useEffect, useState } from "react";
import { Button, Confirm, Icon, Label, List, Message } from "semantic-ui-react";
import { useNavigate } from "react-router-dom";
import moment from "moment";
import { useActions } from "../redux/actions/UseActions";
import * as PatientActions from "../redux/actions/PatientActions";
import { ReviewAction, ReviewBatch } from "../models/Patient";
import { PatientStatus } from "../models/PatientStatus";

interface Props {
  // Dashboard: warning + link only. Patients page: full approve / reject controls.
  compact?: boolean;
  onView?: (batch: ReviewBatch) => void;
  onReviewed?: () => void;
}

interface PendingAction {
  batch: ReviewBatch;
  action: ReviewAction;
}

const ACTION_TEXT: { [action in ReviewAction]: string } = {
  approve: "Approve and send to Open Dental",
  reject: "Reject",
  rereview: "Move back to review",
};

const ImportReviewPanel = ({ compact, onView, onReviewed }: Props) => {
  const patientActions = useActions(PatientActions);
  const navigate = useNavigate();
  const [batches, setBatches] = useState<ReviewBatch[]>([]);
  const [pending, setPending] = useState<PendingAction>();
  const [result, setResult] = useState<{ error?: boolean; text: string }>();
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    loadBatches().then();
  }, []);

  const loadBatches = async () => {
    const data = await patientActions.getReviewSummary();
    setBatches(Array.isArray(data) ? data : []);
  };

  const held = batches.filter(
    (b) => b.status === PatientStatus.REVIEW_REQUIRED,
  );
  const rejected = batches.filter((b) => b.status === PatientStatus.REJECTED);
  const heldTotal = held.reduce((n, b) => n + b.count, 0);

  const runAction = async () => {
    if (!pending) return;
    const { batch, action } = pending;
    setPending(undefined);
    setBusy(true);
    const response = await patientActions.reviewPatients({
      action,
      practiceId: batch.practiceId,
      importDate: batch.importDate,
      sync: action === "approve",
    });
    setBusy(false);
    if (!response) {
      setResult({
        error: true,
        text: `Could not update ${batch.practice}. Please try again.`,
      });
    } else {
      const synced =
        action === "approve"
          ? response.syncStarted
            ? " Sync to Open Dental has started."
            : " They will go to Open Dental on the next scheduled run."
          : "";
      setResult({
        text: `${response.updated} ${batch.practice} patients set to ${response.status}.${synced}`,
      });
    }
    await loadBatches();
    onReviewed?.();
  };

  const batchLabel = (b: ReviewBatch) =>
    `${b.practice} - ${b.count.toLocaleString()} patients imported ${moment(b.importDate).format("DD MMM YYYY")}`;

  if (!batches.length && !result) return null;

  if (compact) {
    if (!held.length) return null;
    return (
      <Message warning icon>
        <Icon name="warning sign" />
        <Message.Content>
          <Message.Header>
            {heldTotal.toLocaleString()} patients are waiting for review before
            they go to Open Dental
          </Message.Header>
          <List bulleted>
            {held.map((b) => (
              <List.Item key={`${b.practiceId}_${b.importDate}`}>
                {batchLabel(b)}
              </List.Item>
            ))}
          </List>
          <Button
            size="small"
            color="orange"
            content="Review"
            onClick={() => navigate("/patients")}
          />
        </Message.Content>
      </Message>
    );
  }

  return (
    <div className="mb-3">
      {result && (
        <Message
          positive={!result.error}
          negative={result.error}
          onDismiss={() => setResult(undefined)}
          content={result.text}
        />
      )}
      {held.map((b) => (
        <Message warning key={`${b.practiceId}_${b.importDate}_held`}>
          <Message.Header>
            <Icon name="warning sign" />
            Review required: {batchLabel(b)}
          </Message.Header>
          <p>
            This import is much larger than usual for this practice, so none of
            these patients have been sent to Open Dental.{" "}
            {b.reviewReason && (
              <Label basic size="small" content={b.reviewReason} />
            )}
          </p>
          <Button
            size="small"
            basic
            content="View patients"
            onClick={() => onView?.(b)}
          />
          <Button
            size="small"
            positive
            disabled={busy}
            content="Approve all & sync"
            onClick={() => setPending({ batch: b, action: "approve" })}
          />
          <Button
            size="small"
            negative
            disabled={busy}
            content="Reject all"
            onClick={() => setPending({ batch: b, action: "reject" })}
          />
        </Message>
      ))}
      {rejected.length > 0 && (
        <Message size="small">
          <Message.Header>Rejected imports</Message.Header>
          <List>
            {rejected.map((b) => (
              <List.Item key={`${b.practiceId}_${b.importDate}_rejected`}>
                {batchLabel(b)}{" "}
                <Button
                  size="mini"
                  basic
                  content="View"
                  onClick={() => onView?.(b)}
                />
                <Button
                  size="mini"
                  basic
                  color="orange"
                  disabled={busy}
                  content="Move back to review"
                  onClick={() => setPending({ batch: b, action: "rereview" })}
                />
              </List.Item>
            ))}
          </List>
        </Message>
      )}
      <Confirm
        open={!!pending}
        header={pending && ACTION_TEXT[pending.action]}
        content={
          pending && (
            <div className="p-3">
              <p>
                <b>{batchLabel(pending.batch)}</b>
              </p>
              {pending.action === "approve" && (
                <p>
                  All of these patients will be created in Open Dental. Only
                  approve if this import is correct.
                </p>
              )}
              {pending.action === "reject" && (
                <p>
                  These patients will not be sent to Open Dental. You can move
                  them back to review later.
                </p>
              )}
            </div>
          )
        }
        confirmButton={pending && ACTION_TEXT[pending.action]}
        onCancel={() => setPending(undefined)}
        onConfirm={runAction}
      />
    </div>
  );
};

export default ImportReviewPanel;
